import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2 cursor-pointer group"
            >
              <span className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-book-read-line text-background-50 text-lg"></i>
              </span>
              <span className="text-lg font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors">
                韩漫大全
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  isActive('/')
                    ? 'text-primary-600 bg-primary-100/70'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                首页
              </Link>

              <div className="relative">
                <button
                  onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                    location.pathname.startsWith('/category')
                      ? 'text-primary-600 bg-primary-100/70'
                      : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  }`}
                >
                  分类浏览
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isCategoryOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-xl py-2 overflow-hidden">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        onClick={() => setIsCategoryOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-100/50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <span className="w-6 h-6 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </span>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  isActive('/about')
                    ? 'text-primary-600 bg-primary-100/70'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                关于我们
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors cursor-pointer">
                <i className="ri-search-line"></i>
              </button>
              <button className="px-5 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                下载APP
              </button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 cursor-pointer"
            >
              <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-b border-background-200/70">
            <div className="px-4 py-3 space-y-1">
              <Link
                to="/"
                className={`block px-4 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  isActive('/') ? 'text-primary-600 bg-primary-100/70' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                首页
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`block px-4 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                    location.pathname === `/category/${cat.slug}`
                      ? 'text-primary-600 bg-primary-100/70'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <span className="w-6 h-6 inline-flex items-center justify-center mr-2">
                    <i className={`${cat.icon} text-base`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                className={`block px-4 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  isActive('/about') ? 'text-primary-600 bg-primary-100/70' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}